import React, { createElement, forwardRef } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';

export const HEADING_VARIANTS = {
  H1_EXTRABOLD: 'h1-extrabold',
  H2: 'h2',
  H3_SEMIBOLD: 'h3-semibold',
  H4: 'h4',
  H5: 'h5',
};

const VARIANT_ARRAY = Object.values(HEADING_VARIANTS);

const VALID_VARIANTS = Object.values(HEADING_VARIANTS).reduce(
  (accumulator, value) => ({ ...accumulator, [value]: true }),
  {},
);

const VARIANT_COMPONENTS = {
  [HEADING_VARIANTS.H1_EXTRABOLD]: 'h1',
  [HEADING_VARIANTS.H2]: 'h2',
  [HEADING_VARIANTS.H3_SEMIBOLD]: 'h3',
  [HEADING_VARIANTS.H4]: 'h4',
  [HEADING_VARIANTS.H5]: 'h5',
};

const Heading = ({
  variant,
  component,
  className,
  enableTheme = true,
  children,
  forwardedRef,
  ...props
}) => {
  // Variant was invalid, render nothing
  if (!VALID_VARIANTS[variant]) {
    if (process.env.NODE_ENV === 'development') {
      console.error(
        `Expected \`variant\` prop to be one of ${VARIANT_ARRAY.join(
          ', ',
        )}, but instead received \`${variant}\``,
      );
    }
    return null;
  }

  const classes = cn(className, 'llui-heading', {
    'llui-theme-heading': enableTheme,
    [`llui-heading-${variant}`]: VALID_VARIANTS[variant],
  });

  return createElement(
    component || VARIANT_COMPONENTS[variant],
    { className: classes, ref: forwardedRef, ...props },
    children,
  );
};

Heading.propTypes = {
  children: PropTypes.node,
  component: PropTypes.node,
  className: PropTypes.string,
  enableTheme: PropTypes.bool,
  variant: PropTypes.oneOf(VARIANT_ARRAY).isRequired,
  forwardedRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({ current: PropTypes.instanceOf(Element) }),
  ]),
};

export default forwardRef((props, ref) => (
  <Heading {...props} forwardedRef={ref} />
));
